import {
  Box,
  Flex,
  HStack,
  Icon,
  Image,
  ListItem,
  SimpleGrid,
  Text,
  UnorderedList
} from '@chakra-ui/react';
import bowlUrl from 'assets_2/images/logo/bowl-dark.png';
import logoUrl from 'assets_2/images/logo/insighttimer-white.svg';
import LanguageSelector from 'components_v2024/LanguageSelector';
import React from 'react';
import { FaFacebookF, FaInstagram, FaTiktok, FaYoutube } from 'react-icons/fa';
import FooterCookieButton from './atoms/FooterCookieButton';
import GetAppFooterLink from './atoms/GetAppFooterLink';
import {
  footerColumns,
  footerLegalLinks,
  footerSocialLinks,
  footerTopLinks
} from './constants';

const socialIcons = {
  Facebook: FaFacebookF,
  Instagram: FaInstagram,
  YouTube: FaYoutube,
  TikTok: FaTiktok
};

// Plain anchors on purpose: many of these pages are served outside this SPA,
// so a full navigation lets hosting route them correctly.
interface FooterFluxProps {
  // Extra bottom padding (px) for pages with a sticky bottom bar.
  bottomSpacing?: number;
}

const FooterFlux = ({ bottomSpacing }: FooterFluxProps) => (
  <Box
    as="footer"
    id="footer"
    className="no-global-zoom"
    w="100%"
    bg="footerBackground"
    color="primary.white"
    fontFamily="ProximaNova"
    position="relative"
    zIndex={50}
    pb={bottomSpacing ? `${bottomSpacing}px` : undefined}
  >
    <Box
      mx="auto"
      w="full"
      maxW="1480px"
      px={5}
      pt={{ base: 10, md: 12 }}
      pb={8}
    >
      <HStack spacing={{ base: 5, md: 8 }} flexWrap="wrap" alignItems="center">
        <a href="/" aria-label="Insight Timer home">
          <Image src={bowlUrl} alt="" w="48px" h="32px" objectFit="contain" />
        </a>
        {footerTopLinks.map(item => (
          <a key={item.name} href={item.link}>
            <Text fontSize="sm" lineHeight="1.5" fontWeight="semibold">
              {item.name}
            </Text>
          </a>
        ))}
        <GetAppFooterLink />
      </HStack>

      <SimpleGrid
        columns={{ base: 2, md: 3, lg: 5 }}
        columnGap={6}
        rowGap={10}
        mt={12}
      >
        {footerColumns.map(column => (
          <Box key={column.name}>
            <Text fontSize="md" lineHeight="1.5" mb={3}>
              {column.name}
            </Text>
            <UnorderedList listStyleType="none" m={0} display="grid" gap={3}>
              {column.links.map(item => (
                <ListItem
                  key={item.name}
                  fontSize="sm"
                  color="secondary.lightGrey"
                >
                  <a href={item.link}>
                    <Text
                      fontSize="sm"
                      lineHeight="1.5"
                      _hover={{ color: 'primary.white' }}
                    >
                      {item.name}
                    </Text>
                  </a>
                </ListItem>
              ))}
            </UnorderedList>
          </Box>
        ))}
      </SimpleGrid>

      <Box
        mt={12}
        py={5}
        borderTop="1px solid"
        borderBottom="1px solid"
        borderColor="secondary.darkGrey"
        fontSize="xs"
        color="secondary.lightGrey"
      >
        <Text
          as="span"
          fontSize="xs"
          lineHeight="1.5"
          color="primary.white"
          fontWeight="semibold"
        >
          Crisis support.
        </Text>{' '}
        If you are in a crisis or any other person may be in danger, do not use
        this site.{' '}
        <a href="/help-now">
          <Text
            as="span"
            fontSize="xs"
            lineHeight="1.5"
            color="primary.green"
            fontWeight="semibold"
          >
            Use these resources
          </Text>
        </a>
      </Box>

      <Flex
        py={8}
        gap={6}
        align={{ base: 'flex-start', md: 'center' }}
        justify="space-between"
        direction={{ base: 'column', md: 'row' }}
        borderBottom="1px solid"
        borderColor="secondary.darkGrey"
      >
        <Flex align="center" gap={6} flexWrap="wrap">
          <Image src={logoUrl} alt="Insight Timer" w="136px" h="26px" />
          <Text fontSize="sm" lineHeight="1.5" color="secondary.lightGrey">
            The largest free library on earth. Free. No Ads. No AI.
          </Text>
        </Flex>
        <Flex
          direction="column"
          align={{ base: 'flex-start', md: 'flex-end' }}
          gap={4}
        >
          <HStack spacing={5}>
            {footerSocialLinks.map(item => {
              const SocialIcon = socialIcons[item.name] as React.ElementType;
              return (
                <a
                  key={item.name}
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={item.name}
                >
                  <Icon as={SocialIcon} boxSize="18px" display="block" />
                </a>
              );
            })}
          </HStack>
          <LanguageSelector variant="dark" />
        </Flex>
      </Flex>

      <Flex
        pt={6}
        gap={4}
        justify="space-between"
        direction={{ base: 'column', md: 'row' }}
        fontSize="xs"
        color="secondary.lightGrey"
      >
        <Text fontSize="xs" lineHeight="1.5">
          &copy; {new Date().getFullYear()} Insight Network Inc. All rights
          reserved.
        </Text>
        <HStack spacing={6} flexWrap="wrap">
          {footerLegalLinks.map(item => (
            <a key={item.name} href={item.link}>
              <Text
                fontSize="xs"
                lineHeight="1.5"
                _hover={{ color: 'primary.white' }}
              >
                {item.name}
              </Text>
            </a>
          ))}
          <FooterCookieButton />
        </HStack>
      </Flex>
    </Box>
  </Box>
);

export default FooterFlux;
